import React, { useState } from 'react';
import {
    FileText, Plus, Video, Image as ImageIcon,
    Shield, AlertTriangle, ChevronRight,
    Trash2, Edit2, Sparkles,
    CheckCircle2
} from 'lucide-react';
import { useAnalysis } from '../contexts/AnalysisContext';
import { useAppContext } from '../contexts/AppContext';
import { WorkInstruction } from '../types';
import { getProcessFlows, SavedProcessFlow } from '../services/processFlowService';
import { ModalWrapper } from '../components/ModalWrapper';

const WorkInstructionsPage: React.FC = () => {
    const {
        processSteps,
        workInstructions,
        addWorkInstruction,
        updateWorkInstruction,
        deleteWorkInstruction,
        saveAnalysis,
        extractFromFlow
    } = useAnalysis();

    const { activeProjectName } = useAppContext();

    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingInstruction, setEditingInstruction] = useState<Partial<WorkInstruction> | null>(null);
    const [showAiModal, setShowAiModal] = useState(false);
    const [showLoadFlowModal, setShowLoadFlowModal] = useState(false);
    const [availableFlows, setAvailableFlows] = useState<SavedProcessFlow[]>([]);
    const [isLoadingFlows, setIsLoadingFlows] = useState(false);
    const [selectedFlowId, setSelectedFlowId] = useState<string>('');
    const [isImporting, setIsImporting] = useState(false);

    const selectedStep = processSteps.find(s => s.id === selectedStepId);
    const instruction = workInstructions.find(i => i.processStepId === selectedStepId);

    // Load process flows for the active project
    React.useEffect(() => {
        const loadFlows = async () => {
            if (activeProjectName) {
                setIsLoadingFlows(true);
                try {
                    const flows = await getProcessFlows(activeProjectName);
                    setAvailableFlows(flows);
                } catch (error) {
                    console.error('Failed to load process flows:', error);
                } finally {
                    setIsLoadingFlows(false);
                }
            }
        };
        loadFlows();
    }, [activeProjectName]);

    const handleImportFromFlow = async () => {
        if (!selectedFlowId) return;

        const flow = availableFlows.find(f => f.id === selectedFlowId);
        if (flow) {
            setIsImporting(true);
            try {
                // This updates the AnalysisContext processSteps
                extractFromFlow(flow.flow.nodes);
                await saveAnalysis();
                setShowLoadFlowModal(false);
                setSelectedFlowId('');
            } catch (error) {
                console.error('Failed to import steps:', error);
                alert('Failed to import process steps');
            } finally {
                setIsImporting(false);
            }
        }
    };

    const handleCreateNew = () => {
        if (!selectedStep) return;

        const newInstr: WorkInstruction = {
            id: `wi-${Date.now()}`,
            processStepId: selectedStep.id,
            stepNumber: selectedStep.stepNumber,
            stepName: selectedStep.stepName,
            description: selectedStep.description || '',
            hazards: [],
            ppe: [],
            steps: [
                { order: 1, instruction: 'Initial setup and preparation' }
            ],
            lastUpdated: new Date().toISOString(),
            projectName: activeProjectName || ''
        };

        setEditingInstruction(newInstr);
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!editingInstruction) return;

        if (instruction) {
            updateWorkInstruction(instruction.id, editingInstruction);
        } else {
            addWorkInstruction(editingInstruction as WorkInstruction);
        }

        await saveAnalysis();
        setIsEditing(false);
        setEditingInstruction(null);
    };

    const handleAiGenerate = () => {
        if (!selectedStep) return;

        // Mock AI generation
        const aiDraft: Partial<WorkInstruction> = {
            ...editingInstruction,
            description: `Standard operating procedure for ${selectedStep.stepName}. Ensures consistent quality and safety during ${selectedStep.stepType} operations.`,
            hazards: ['Pinch points', 'Hot surfaces', 'Sharp edges'],
            ppe: ['Safety glasses', 'Cut-resistant gloves', 'Steel-toe boots'],
            steps: [
                { order: 1, instruction: 'Verify all materials and components are available per BOM.' },
                { order: 2, instruction: `Position the part securely in the ${selectedStep.equipment || 'fixture'}.` },
                { order: 3, instruction: `Perform ${selectedStep.stepName} operation according to specified parameters.` },
                { order: 4, instruction: 'Visually inspect for defects or non-conformances.' },
                { order: 5, instruction: 'Move completed part to the next station or storage.' }
            ]
        };

        setEditingInstruction(aiDraft);
        setShowAiModal(false);
    };

    return (
        <div className="flex h-[calc(100vh-12rem)] gap-6">
            {/* Sidebar: Process Steps */}
            <div className="w-80 flex flex-col bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] overflow-hidden">
                <div className="p-4 border-b border-[var(--border-color)] bg-[var(--bg-primary)]/50">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <FileText size={18} className="text-primary-500" />
                            Process Steps
                        </h2>
                        <button
                            onClick={() => setShowLoadFlowModal(true)}
                            className="p-1.5 hover:bg-[var(--bg-primary)] rounded-lg text-primary-500 transition-colors"
                            title="Load steps from Process Flow"
                        >
                            <Sparkles size={16} />
                        </button>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)]">Select a step to view/edit instructions</p>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {processSteps.map(step => {
                        const hasInstruction = workInstructions.some(i => i.processStepId === step.id);
                        return (
                            <button
                                key={step.id}
                                onClick={() => {
                                    setSelectedStepId(step.id);
                                    setIsEditing(false);
                                }}
                                className={`w-full text-left p-3 rounded-lg transition-all flex items-center justify-between group ${selectedStepId === step.id
                                    ? 'bg-primary-500/10 border border-primary-500/30'
                                    : 'hover:bg-[var(--bg-primary)] border border-transparent'
                                    }`}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono bg-[var(--bg-primary)] px-1.5 py-0.5 rounded border border-[var(--border-color)] text-[var(--text-secondary)]">
                                            {step.stepNumber}
                                        </span>
                                        {hasInstruction && (
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0" />
                                        )}
                                    </div>
                                    <h3 className={`text-sm font-medium truncate mt-1 ${selectedStepId === step.id ? 'text-primary-500' : 'text-[var(--text-primary)]'
                                        }`}>
                                        {step.stepName}
                                    </h3>
                                </div>
                                <ChevronRight
                                    size={16}
                                    className={`shrink-0 transition-transform ${selectedStepId === step.id ? 'text-primary-500 translate-x-1' : 'text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]'
                                        }`}
                                />
                            </button>
                        );
                    })}
                    {processSteps.length === 0 && (
                        <div className="text-center py-8 px-4">
                            <p className="text-sm text-[var(--text-muted)] italic">No process steps defined yet. Go to Process Flow to add steps.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] overflow-hidden">
                {!selectedStepId ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                        <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mb-4">
                            <FileText className="text-primary-500" size={32} />
                        </div>
                        <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">Select a Process Step</h2>
                        <p className="text-[var(--text-secondary)] max-w-sm mx-auto">
                            Choose a manufacturing step from the sidebar to create or manage its detailed work instructions.
                        </p>
                    </div>
                ) : isEditing ? (
                    /* Editor Mode */
                    <div className="flex flex-col h-full">
                        <div className="p-4 border-b border-[var(--border-color)] flex items-center justify-between bg-[var(--bg-primary)]/50">
                            <div>
                                <h2 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                                    <Edit2 size={18} className="text-primary-500" />
                                    Editing Instruction: {selectedStep?.stepNumber}
                                </h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowAiModal(true)}
                                    className="px-3 py-1.5 bg-purple-500/10 text-purple-500 rounded-lg hover:bg-purple-500/20 transition-colors flex items-center gap-2 text-sm font-medium border border-purple-500/20"
                                >
                                    <Sparkles size={16} />
                                    AI Assist
                                </button>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="px-3 py-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] rounded-lg transition-colors text-sm font-medium border border-[var(--border-color)]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-[var(--text-muted)] uppercase mb-1.5">Description</label>
                                    <textarea
                                        className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-none h-24"
                                        placeholder="Brief overview of the operation..."
                                        value={editingInstruction?.description}
                                        onChange={e => setEditingInstruction(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-[var(--text-muted)] uppercase mb-1.5 flex items-center gap-2">
                                            <Shield size={14} className="text-blue-500" />
                                            Required PPE
                                        </label>
                                        <input
                                            className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                            placeholder="e.g. Safety Glasses, Gloves (comma separated)"
                                            value={editingInstruction?.ppe?.join(', ')}
                                            onChange={e => setEditingInstruction(prev => ({ ...prev, ppe: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-[var(--text-muted)] uppercase mb-1.5 flex items-center gap-2">
                                            <AlertTriangle size={14} className="text-amber-500" />
                                            Hazards & Risks
                                        </label>
                                        <input
                                            className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                            placeholder="e.g. Sharp Edges, High Temp (comma separated)"
                                            value={editingInstruction?.hazards?.join(', ')}
                                            onChange={e => setEditingInstruction(prev => ({ ...prev, hazards: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Step by Step */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-xs font-bold text-[var(--text-muted)] uppercase">Instruction Steps</label>
                                    <button
                                        onClick={() => setEditingInstruction(prev => ({
                                            ...prev,
                                            steps: [...(prev?.steps || []), { order: (prev?.steps?.length || 0) + 1, instruction: '' }]
                                        }))}
                                        className="text-xs font-bold text-primary-500 hover:text-primary-600 flex items-center gap-1"
                                    >
                                        <Plus size={14} />
                                        Add Step
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {editingInstruction?.steps?.map((step, idx) => (
                                        <div key={idx} className="flex gap-3 items-start bg-[var(--bg-primary)] p-4 rounded-xl border border-[var(--border-color)] group">
                                            <div className="w-6 h-6 rounded-full bg-primary-500/10 text-primary-500 flex items-center justify-center text-xs font-bold shrink-0">
                                                {idx + 1}
                                            </div>
                                            <div className="flex-1 space-y-3">
                                                <textarea
                                                    className="w-full bg-transparent border-none p-0 text-sm text-[var(--text-primary)] focus:ring-0 outline-none transition-all resize-none min-h-[3rem]"
                                                    placeholder="Step instruction..."
                                                    value={step.instruction}
                                                    onChange={e => {
                                                        const newSteps = [...(editingInstruction.steps || [])];
                                                        newSteps[idx] = { ...step, instruction: e.target.value };
                                                        setEditingInstruction(prev => ({ ...prev, steps: newSteps }));
                                                    }}
                                                />
                                                <div className="flex items-center gap-4 border-t border-[var(--border-color)]/50 pt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button className="text-[var(--text-muted)] hover:text-primary-500 transition-colors flex items-center gap-1.5 text-xs font-medium">
                                                        <ImageIcon size={14} /> Add Image
                                                    </button>
                                                    <button className="text-[var(--text-muted)] hover:text-purple-500 transition-colors flex items-center gap-1.5 text-xs font-medium">
                                                        <Video size={14} /> Add Video
                                                    </button>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const newSteps = (editingInstruction.steps || []).filter((_, i) => i !== idx);
                                                    setEditingInstruction(prev => ({ ...prev, steps: newSteps }));
                                                }}
                                                className="text-[var(--text-muted)] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : instruction ? (
                    /* Display Mode */
                    <div className="flex flex-col h-full bg-[var(--bg-primary)]/20">
                        <div className="p-6 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <span className="text-xs font-mono bg-blue-500/10 text-blue-500 px-2 py-0.5 rounded border border-blue-500/20">
                                        WORK INSTRUCTION
                                    </span>
                                    <span className="text-xs text-[var(--text-muted)]">
                                        Last Updated: {new Date(instruction.lastUpdated).toLocaleDateString()}
                                    </span>
                                </div>
                                <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                                    {selectedStep?.stepNumber}: {selectedStep?.stepName}
                                </h2>
                                <p className="text-[var(--text-secondary)] mt-2 italic max-w-2xl border-l-2 border-primary-500 pl-3">
                                    {instruction.description}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        if (confirm('Are you sure you want to delete this work instruction?')) {
                                            deleteWorkInstruction(instruction.id);
                                            setSelectedStepId(null);
                                        }
                                    }}
                                    className="p-2.5 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors border border-red-500/20"
                                    title="Delete Instruction"
                                >
                                    <Trash2 size={18} />
                                </button>
                                <div className="w-px h-8 bg-[var(--border-color)] mx-1" />
                                <button
                                    onClick={() => {
                                        setEditingInstruction(instruction);
                                        setIsEditing(true);
                                    }}
                                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 font-medium"
                                >
                                    <Edit2 size={18} />
                                    Edit
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 space-y-8 max-w-5xl mx-auto w-full">
                            {/* Safety & PPE Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-4 opacity-5 bg-amber-500 rounded-bl-3xl">
                                        <AlertTriangle size={64} />
                                    </div>
                                    <h3 className="text-amber-500 font-bold uppercase text-xs tracking-wider mb-4 flex items-center gap-2">
                                        <AlertTriangle size={16} />
                                        Safety Hazards
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {instruction.hazards.length > 0 ? instruction.hazards.map((h, i) => (
                                            <span key={i} className="bg-amber-500/10 text-amber-600 px-3 py-1 rounded-full text-xs font-bold border border-amber-500/10">
                                                {h}
                                            </span>
                                        )) : <span className="text-[var(--text-muted)] text-sm italic">No specific hazards identified</span>}
                                    </div>
                                </div>
                                <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-5 bg-blue-500 rounded-bl-3xl">
                                        <Shield size={64} />
                                    </div>
                                    <h3 className="text-blue-500 font-bold uppercase text-xs tracking-wider mb-4 flex items-center gap-2">
                                        <Shield size={16} />
                                        Required PPE
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {instruction.ppe.length > 0 ? instruction.ppe.map((p, i) => (
                                            <span key={i} className="bg-blue-500/10 text-blue-600 px-3 py-1 rounded-full text-xs font-bold border border-blue-500/10">
                                                {p}
                                            </span>
                                        )) : <span className="text-[var(--text-muted)] text-sm italic">Standard PPE required</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Procedure Steps */}
                            <div className="space-y-4">
                                <h3 className="text-[var(--text-primary)] font-bold text-lg mb-6 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center text-white">
                                        <CheckCircle2 size={20} />
                                    </span>
                                    Standard Operating Procedure
                                </h3>
                                <div className="space-y-6">
                                    {instruction.steps.map((step, idx) => (
                                        <div key={idx} className="flex gap-6 items-start">
                                            <div className="relative">
                                                <div className="w-10 h-10 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] flex items-center justify-center font-bold text-lg shadow-sm z-10 relative">
                                                    {idx + 1}
                                                </div>
                                                {idx < instruction.steps.length - 1 && (
                                                    <div className="absolute top-10 bottom-[-1.5rem] left-1/2 w-0.5 bg-[var(--border-color)] -translate-x-1/2" />
                                                )}
                                            </div>
                                            <div className="flex-1 bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-6 shadow-sm hover:border-primary-500/30 transition-colors">
                                                <p className="text-[var(--text-primary)] text-base leading-relaxed">
                                                    {step.instruction}
                                                </p>
                                                {(step.image || step.videoUrl) && (
                                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                                        {step.image && (
                                                            <div className="aspect-video bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] flex items-center justify-center">
                                                                <ImageIcon className="text-[var(--text-muted)]" />
                                                            </div>
                                                        )}
                                                        {step.videoUrl && (
                                                            <div className="aspect-video bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] flex items-center justify-center">
                                                                <Video className="text-[var(--text-muted)]" />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Initial Create State */
                    <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                        <div className="w-20 h-20 bg-primary-500/10 rounded-2xl flex items-center justify-center mb-6 border border-primary-500/20 rotate-3">
                            <Plus className="text-primary-500" size={32} />
                        </div>
                        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">No Instructions for {selectedStep?.stepNumber}</h2>
                        <p className="text-[var(--text-secondary)] max-w-md mx-auto mb-8">
                            Detailed work instructions ensure process stability and operator safety. Create the first version for this operation.
                        </p>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={handleCreateNew}
                                className="px-6 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all font-bold shadow-lg shadow-primary-500/20 flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <Plus size={20} />
                                Create Instruction
                            </button>
                            <button
                                onClick={() => {
                                    handleCreateNew();
                                    setTimeout(() => setShowAiModal(true), 100);
                                }}
                                className="px-6 py-3 bg-purple-500/10 text-purple-500 rounded-xl hover:bg-purple-500/20 transition-all font-bold border border-purple-500/20 flex items-center gap-2"
                            >
                                <Sparkles size={20} />
                                Generate with AI
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* AI Generation Modal */}
            {showAiModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-[var(--border-color)] bg-purple-500/5">
                            <h2 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                                <Sparkles className="text-purple-500" />
                                AI Instruction Draft
                            </h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">
                                Generates a standard operating procedure based on the process step details.
                            </p>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                <div className="p-4 bg-[var(--bg-primary)] rounded-xl border border-[var(--border-color)]">
                                    <h4 className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2">Target Operation</h4>
                                    <p className="text-sm font-medium text-[var(--text-primary)]">
                                        {selectedStep?.stepNumber}: {selectedStep?.stepName}
                                    </p>
                                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                                        {selectedStep?.description}
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
                                        <input type="checkbox" defaultChecked className="rounded border-[var(--border-color)] text-primary-500" />
                                        Include default industry PPE
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
                                        <input type="checkbox" defaultChecked className="rounded border-[var(--border-color)] text-primary-500" />
                                        Analyze safety hazards
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
                                        <input type="checkbox" defaultChecked className="rounded border-[var(--border-color)] text-primary-500" />
                                        Focus on quality control points
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-[var(--bg-primary)]/50 border-t border-[var(--border-color)] flex justify-end gap-3">
                            <button
                                onClick={() => setShowAiModal(false)}
                                className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAiGenerate}
                                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-bold shadow-lg shadow-purple-500/20"
                            >
                                Generate Draft
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Load Flow Modal */}
            <ModalWrapper
                isOpen={showLoadFlowModal}
                onClose={() => setShowLoadFlowModal(false)}
                title="Load Steps from Process Flow"
                maxWidth="max-w-md"
            >
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-2">
                            Select Process Flow
                        </label>
                        <select
                            value={selectedFlowId}
                            onChange={(e) => setSelectedFlowId(e.target.value)}
                            className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-[var(--text-primary)] focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        >
                            <option value="">-- Choose a Process Flow --</option>
                            {availableFlows.map(flow => (
                                <option key={flow.id} value={flow.id}>{flow.name}</option>
                            ))}
                        </select>
                        {availableFlows.length === 0 && !isLoadingFlows && (
                            <p className="mt-2 text-xs text-amber-500">No process flows found for this project.</p>
                        )}
                    </div>

                    <div className="p-4 bg-primary-500/5 rounded-xl border border-primary-500/10">
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                            Loading a process flow will automatically extract all process steps and add them to your work instructions list.
                            Existing steps will be preserved.
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-color)]">
                        <button
                            onClick={() => setShowLoadFlowModal(false)}
                            className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleImportFromFlow}
                            disabled={!selectedFlowId || isImporting}
                            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-bold shadow-lg shadow-primary-500/20 disabled:opacity-50"
                        >
                            {isImporting ? 'Importing...' : 'Import Steps'}
                        </button>
                    </div>
                </div>
            </ModalWrapper>
        </div>
    );
};

export default WorkInstructionsPage;
